// ============================================
// FILE: frontend/src/api/matching.api.js
// PURPOSE: API calls for matching algorithm operations
// ============================================

import apiClient from './client.js';

console.log('[matching.api] Initializing matching API');

// ============================================
// SECTION: MATCHING OPERATIONS
// ============================================

/**
 * Trigger manual matching algorithm
 * Admin runs this to find all 90%+ matches
 */
export const runMatching = async () => {
  console.log('[matching.api] 🔧 Calling: POST /api/matching/run');
  
  try {
    const response = await apiClient.post('/matching/run');
    console.log('[matching.api] ✅ Matching run successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('[matching.api] ❌ Matching run failed:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: FETCH MATCHES
// ============================================

/**
 * Get all matches for admin review
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Items per page (default 20)
 * @param {string} status - Filter by status (default: pending_review)
 * @param {string} sortBy - Sort order (score, newest, oldest)
 */
export const getMatches = async (page = 1, limit = 20, status = 'pending_review', sortBy = 'score') => {
  console.log('[matching.api] 📋 Calling: GET /api/matching/matches', {
    page,
    limit,
    status,
    sortBy
  });

  try {
    const response = await apiClient.get('/matching/matches', {
      params: { page, limit, status, sortBy }
    });
    console.log('[matching.api] ✅ Matches fetched:', {
      count: response.data.matches?.length,
      total: response.data.pagination?.total
    });
    return response.data;
  } catch (error) {
    console.error('[matching.api] ❌ Failed to fetch matches:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: FETCH SINGLE MATCH
// ============================================

/**
 * Get detailed match information
 * @param {string} matchId - Match ID
 */
export const getMatchDetails = async (matchId) => {
  console.log('[matching.api] 🔍 Calling: GET /api/matching/matches/:id', { matchId });

  try {
    const response = await apiClient.get(`/matching/matches/${matchId}`);
    console.log('[matching.api] ✅ Match details retrieved:', {
      score: response.data.match?.matchScore,
      status: response.data.match?.status
    });
    return response.data;
  } catch (error) {
    console.error('[matching.api] ❌ Failed to fetch match details:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: REVIEW MATCH (APPROVE/REJECT)
// ============================================

/**
 * Admin reviews and approves or rejects a match
 * @param {string} matchId - Match ID
 * @param {string} action - "approve" or "reject"
 * @param {string} adminNotes - Admin notes (optional)
 */
export const reviewMatch = async (matchId, action, adminNotes = '') => {
  console.log('[matching.api] 📝 Calling: PUT /api/matching/matches/:id/review', {
    matchId,
    action,
    notesLength: adminNotes.length
  });

  try {
    const response = await apiClient.put(`/matching/matches/${matchId}/review`, {
      action,
      adminNotes
    });
    console.log('[matching.api] ✅ Match reviewed:', {
      newStatus: response.data.match?.status,
      action
    });
    return response.data;
  } catch (error) {
    console.error('[matching.api] ❌ Failed to review match:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: MATCHING ANALYTICS
// ============================================

/**
 * Get all matching attempts (analytics/history)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 */
export const getMatchingAttempts = async (page = 1, limit = 50) => {
  console.log('[matching.api] 📊 Calling: GET /api/matching/attempts', {
    page,
    limit
  });

  try {
    const response = await apiClient.get('/matching/attempts', {
      params: { page, limit }
    });
    console.log('[matching.api] ✅ Attempts retrieved:', {
      count: response.data.attempts?.length,
      statistics: response.data.statistics
    });
    return response.data;
  } catch (error) {
    console.error('[matching.api] ❌ Failed to fetch attempts:', error.response?.data);
    throw error;
  }
};

// ============================================
// EXPORTS
// ============================================

export const matchingAPI = {
  runMatching,
  getMatches,
  getMatchDetails,
  reviewMatch,
  getMatchingAttempts
};

export default matchingAPI;